import { useState } from "react";
import axios from "axios";
import { Field, inputCls, textareaCls, Honeypot } from "@/components/FormControls";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const helpForOptions = ["My business", "My organization", "My home", "My family", "Something else"];

export const RequestForm = ({ onSuccess }) => {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", city: "",
    help_for: "", problem: "", experience_kind: "", timing: "", website: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      await axios.post(`${API}/experience-requests`, form);
      setStatus("success");
      onSuccess();
      window.scrollTo(0, 0);
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong sending your request. Please check the required fields and try again.");
    }
  };

  return (
    <form onSubmit={submit} className="mt-12 space-y-8" data-testid="find-experience-form">
      <Field label="Name" id="name" required>
        <input id="name" data-testid="request-name-input" className={inputCls} value={form.name} onChange={set("name")} required />
      </Field>
      <Field label="Company/organization (if applicable)" id="company">
        <input id="company" data-testid="request-company-input" className={inputCls} value={form.company} onChange={set("company")} />
      </Field>
      <div className="grid gap-8 sm:grid-cols-2">
        <Field label="Email" id="email" required>
          <input id="email" type="email" data-testid="request-email-input" className={inputCls} value={form.email} onChange={set("email")} required />
        </Field>
        <Field label="Phone" id="phone">
          <input id="phone" type="tel" data-testid="request-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} />
        </Field>
      </div>
      <Field label="City" id="city">
        <input id="city" data-testid="request-city-input" className={inputCls} value={form.city} onChange={set("city")} />
      </Field>

      <fieldset>
        <legend className="text-lg font-semibold text-foreground mb-3">I am looking for help for:</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {helpForOptions.map((opt) => (
            <label
              key={opt}
              className="flex items-center gap-3 bg-card border border-border rounded-md px-4 py-3 cursor-pointer transition-colors duration-200 hover:border-secondary"
            >
              <input
                type="radio"
                name="help_for"
                data-testid={`request-helpfor-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="h-6 w-6 shrink-0 accent-[#0A192F]"
                checked={form.help_for === opt}
                onChange={() => setForm({ ...form, help_for: opt })}
              />
              <span className="text-lg">{opt}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <Field label="What are you trying to solve, improve, evaluate, teach or accomplish?" id="problem" required>
        <textarea id="problem" data-testid="request-problem-textarea" className={textareaCls} value={form.problem} onChange={set("problem")} required />
      </Field>
      <Field label="What kind of experience do you think would help?" id="experience_kind">
        <textarea id="experience_kind" data-testid="request-experience-kind-textarea" className={textareaCls} value={form.experience_kind} onChange={set("experience_kind")} />
      </Field>
      <Field label="When would you ideally like help? (optional)" id="timing">
        <input id="timing" data-testid="request-timing-input" className={inputCls} value={form.timing} onChange={set("timing")} />
      </Field>

      <Honeypot value={form.website} onChange={(v) => setForm({ ...form, website: v })} />

      {error && (
        <p className="text-destructive text-lg font-semibold" role="alert" data-testid="request-error-message">
          {error}
        </p>
      )}

      <button
        type="submit"
        data-testid="find-experience-submit-button"
        disabled={status === "submitting"}
        className="w-full sm:w-auto bg-primary text-primary-foreground text-xl font-semibold px-12 py-5 rounded-full transition-[background-color,transform] duration-300 hover:bg-[#15325a] hover:-translate-y-0.5 disabled:opacity-60"
      >
        {status === "submitting" ? "SENDING..." : "FIND AN ADVISOR"}
      </button>
    </form>
  );
};
